import React from 'react';
import type { DealMedication, DealMedicationSummary } from '../data/api';
import { LISTED_DISEASES } from '../data/api';

const label = (code: string) =>
  LISTED_DISEASES.find((d) => d.code === code)?.label || code;

/**
 * Renders a three-state answer honestly.
 *
 * Null means the question was never asked, and that is not the same as "no".
 * Showing a blank or an unticked box for an unanswered health question would
 * read as the seller having denied it — which for a disease declaration is a
 * materially different (and potentially damaging) statement.
 */
const Answer: React.FC<{ value?: boolean | null }> = ({ value }) => {
  if (value === null || value === undefined) {
    return <span style={{ opacity: 0.55 }}>Not answered</span>;
  }
  return value
    ? <span style={{ color: '#106b38' }}>Yes</span>
    : <span style={{ color: '#8a4b00' }}>No</span>;
};

const Row: React.FC<{ question: string; children: React.ReactNode }> = ({ question, children }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    padding: '5px 0',
    fontSize: 14,
    borderBottom: '1px solid rgba(30,107,184,0.08)',
  }}>
    <span>{question}</span>
    <span style={{ textAlign: 'right' }}>{children}</span>
  </div>
);

export const DealHealthPanel: React.FC<{
  deal: any;
  medications: DealMedication[];
  medicationSummary?: DealMedicationSummary | null;
}> = ({ deal, medications, medicationSummary }) => {
  const diseases: string[] = Array.isArray(deal?.diseases_present) ? deal.diseases_present : [];
  const byProducts: string[] = Array.isArray(deal?.animal_by_products) ? deal.animal_by_products : [];

  return (
    <div style={{ fontSize: 14 }}>
      <Row question="Individually examined before and upon loading">
        <Answer value={deal?.examined_before_and_on_loading} />
      </Row>
      <Row question="Free of clinical signs of the listed diseases">
        <Answer value={deal?.free_of_listed_diseases} />
      </Row>

      {diseases.length > 0 && (
        <div style={{ padding: '8px 0' }}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.65 }}>
            Clinical signs present
          </div>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {diseases.map((code) => (
              <li key={code} style={{ color: '#6b1010' }}>{label(code)}</li>
            ))}
          </ul>
        </div>
      )}
      {deal?.diseases_other ? (
        <Row question="Other conditions">{deal.diseases_other}</Row>
      ) : null}

      <Row question="Clinical examinations report available">
        <Answer value={deal?.clinical_report_available} />
      </Row>
      <Row question="Withdrawal periods adhered to">
        <Answer value={deal?.withdrawal_periods_adhered} />
      </Row>
      <Row question="Purchaser requires documents of their own">
        <Answer value={deal?.purchaser_documents_required} />
      </Row>
      {deal?.purchaser_documents_required ? (
        <Row question="Purchaser documents completed">
          <Answer value={deal?.purchaser_documents_completed} />
        </Row>
      ) : null}

      {/* Medications. The headline is the LATEST clearance across every
          product, because the deal is only safe once the last one has cleared
          — a per-medication list alone would let the earliest date read as
          reassuring. */}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.65 }}>
          Medications
        </div>
        {!medications.length ? (
          <p style={{ margin: '6px 0 0', opacity: 0.7 }}>None recorded.</p>
        ) : (
          <>
            {medicationSummary && (
              <p style={{ margin: '6px 0 8px' }}>
                {medicationSummary.allCleared === null ? (
                  <span style={{ color: '#8a4b00' }}>
                    Withdrawal status unknown — a medication is missing its
                    treatment date or withdrawal period.
                  </span>
                ) : medicationSummary.allCleared ? (
                  <span style={{ color: '#106b38' }}>
                    All withdrawal periods cleared
                    {medicationSummary.latestClearsOn ? ` (last cleared ${medicationSummary.latestClearsOn})` : ''}.
                  </span>
                ) : (
                  <span style={{ color: '#6b1010' }}>
                    Not clear until {medicationSummary.latestClearsOn}
                    {typeof medicationSummary.daysUntilClear === 'number'
                      ? ` — ${medicationSummary.daysUntilClear} day(s) to go`
                      : ''}.
                  </span>
                )}
              </p>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', opacity: 0.7 }}>
                    <th style={{ padding: '4px 8px 4px 0' }}>Medication</th>
                    <th style={{ padding: '4px 8px' }}>Antibiotic</th>
                    <th style={{ padding: '4px 8px' }}>Last treated</th>
                    <th style={{ padding: '4px 8px' }}>Withdrawal</th>
                    <th style={{ padding: '4px 8px' }}>Clears</th>
                    <th style={{ padding: '4px 8px' }}>Days since</th>
                  </tr>
                </thead>
                <tbody>
                  {medications.map((med) => (
                    <tr key={med.id} style={{ borderTop: '1px solid rgba(30,107,184,0.12)' }}>
                      <td style={{ padding: '4px 8px 4px 0' }}>{med.displayName}</td>
                      <td style={{ padding: '4px 8px' }}>{med.isAntibiotic ? 'Yes' : '—'}</td>
                      <td style={{ padding: '4px 8px' }}>{med.lastTreatmentDate || '—'}</td>
                      <td style={{ padding: '4px 8px' }}>
                        {med.effectiveWithdrawalDays != null ? `${med.effectiveWithdrawalDays} d` : '—'}
                      </td>
                      <td style={{ padding: '4px 8px' }}>{med.clearsOn || '—'}</td>
                      <td style={{ padding: '4px 8px' }}>
                        {med.daysSinceTreatment != null ? med.daysSinceTreatment : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Feeding. Deliberately labelled as a declaration — it is what the
          seller states about the 90 days before the deal, not what AVCS
          measured in a feedlot. */}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.65 }}>
          Feeding declaration (seller's statement, last 90 days)
        </div>
        <Row question="Feeding system">
          {deal?.feeding_system
            ? (deal.feeding_system === 'OTHER' && deal.feeding_system_other
              ? deal.feeding_system_other
              : deal.feeding_system)
            : <span style={{ opacity: 0.55 }}>Not answered</span>}
        </Row>
        {Array.isArray(deal?.feeding_declarations) && deal.feeding_declarations.length > 0 && (
          <div style={{ marginTop: 4 }}>
            <Row question={deal.feeding_declarations.length === 1 ? 'Declared feed' : 'Declared feeds'}>
              {''}
            </Row>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {deal.feeding_declarations.map((feed: any) => (
                <li key={feed.id}>
                  {feed.feed_name}
                  {feed.nutritional_value ? ` — ${feed.nutritional_value}` : ''}
                  {feed.introduced_date
                    ? ` (introduced ${feed.introduced_date}${typeof feed.days_since_introduced === 'number' ? `, ${feed.days_since_introduced} days before sale` : ''})`
                    : ''}
                  {feed.description ? (
                    <div style={{ fontSize: 12, opacity: 0.65 }}>{feed.description}</div>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        )}
        <Row question="Animal by-products fed">
          <Answer value={deal?.animal_by_products_fed} />
        </Row>
        {byProducts.length > 0 && (
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {byProducts.map((code) => <li key={code}>{code}</li>)}
          </ul>
        )}
        {deal?.animal_by_products_other ? (
          <Row question="Other by-product">{deal.animal_by_products_other}</Row>
        ) : null}
      </div>
    </div>
  );
};
